/**
 * items.js — reading a board item: its status, urgency, display order, search, and the
 * "most needed" insight. An item is one dailyRestockItems doc (see data/items.js).
 */

import { STATUS, FLAG_STATUSES } from '../config/lists';
import { TOP_NEEDED_COUNT } from '../config/constants';
import { normalizeName } from './names';
import { timeMs } from './dates';

/** Effective status — new items carry `status`; older ones fall back to `done`. */
export function itemStatus(item) {
  if (item && item.status) return item.status;
  return item && item.done ? STATUS.REFILLED : STATUS.NEEDED;
}

export const isHandled = item => itemStatus(item) === STATUS.REFILLED;

export const isFlagged = item => FLAG_STATUSES.includes(itemStatus(item));

export const isUrgent = item => !!(item && item.urgent);

/** Display order: urgent first, then oldest-added first. */
export function sortItems(items) {
  return [...(items || [])].sort(
    (a, b) => (isUrgent(b) - isUrgent(a)) || (timeMs(a.addedAt) - timeMs(b.addedAt)),
  );
}

/** Filter items by free-text name query and an optional category id ('all' = any). */
export function filterItems(items, queryStr = '', category = 'all') {
  const q = normalizeName(queryStr);
  return (items || []).filter(it => {
    if (category !== 'all' && it.category !== category) return false;
    if (q && !normalizeName(it.name).includes(q)) return false;
    return true;
  });
}

/** Most-frequently-needed items across a window (History insight). */
export function topNeeded(items, n = TOP_NEEDED_COUNT) {
  const byKey = new Map();
  for (const it of items || []) {
    const key = it.category + '::' + normalizeName(it.name);
    if (!normalizeName(it.name)) continue;
    const prev = byKey.get(key) || { name: it.name, category: it.category, days: new Set() };
    prev.days.add(it.date);
    prev.name = it.name;
    byKey.set(key, prev);
  }
  return [...byKey.values()]
    .map(e => ({ name: e.name, category: e.category, count: e.days.size }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, n);
}
